import { api } from '../api.ts';
import { html, raw, th, type Raw } from '../html.ts';
import { ACTION, ENTITY, formatDateTime, t } from '../i18n.ts';
import { emptyState } from '../ui.ts';

export function pageHeader(eyebrow: string, title: string, description?: string, actions?: Raw, variant = 'compact'): Raw {
  return html`<header class="p-content-header pma-page-header" data-p-variant="${variant}">
    <div class="p-content-header__row">
      <div class="p-content-header__copy">
        <p class="p-content-header__eyebrow">${eyebrow}</p>
        <h1>${title}</h1>
        ${description ? html`<p>${description}</p>` : ''}
      </div>
      ${actions ? html`<div class="p-content-header__actions">${actions}</div>` : ''}
    </div>
  </header>`;
}

export function projectLink(p: { project_id?: string; id?: string; project_key?: string; key?: string }, label?: string): Raw {
  const id = p.project_id ?? p.id;
  return html`<a href="/projects/${id}">${label ?? p.project_key ?? p.key}</a>`;
}

export interface EventRow {
  id: number;
  occurred_at: string;
  actor_type: string;
  actor_name: string | null;
  entity_type: string;
  action: string;
  label: string | null;
  project_id: string | null;
  project_key: string | null;
  changes: Record<string, [unknown, unknown]> | null;
}

export function eventLine(event: EventRow, withProject = true): Raw {
  const actor = event.actor_type === 'api_token' ? t('Integration') : event.actor_type === 'connector' ? t('Connector') : event.actor_type === 'system' ? t('System') : event.actor_name ?? t('Gelöschte Person');
  const changed = event.changes ? Object.keys(event.changes) : [];
  return html`<li>
    <span class="pma-timeline__dot" aria-hidden="true"></span>
    <div>
      <time datetime="${event.occurred_at}">${formatDateTime(event.occurred_at)}</time>
      <p>${th(t('{actor} hat {entity} {label} {action}'), {
        actor: html`<strong>${actor}</strong>`,
        entity: ENTITY[event.entity_type] ?? event.entity_type,
        label: event.label ? html`<q>${event.label}</q>` : '',
        action: ACTION[event.action] ?? event.action,
      })}
        ${withProject && event.project_key && event.project_id ? html` · ${projectLink({ project_id: event.project_id, project_key: event.project_key })}` : ''}
      </p>
      ${changed.length ? html`<p class="pma-muted pma-small">${t('Felder')}: ${changed.join(', ')}</p>` : ''}
    </div>
  </li>`;
}

export async function renderActivity(target: HTMLElement, projectId?: string, limit = 30): Promise<void> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (projectId) query.set('projectId', projectId);
  const { items } = await api.get<{ items: EventRow[] }>(`/events?${query}`);
  target.innerHTML = items.length
    ? html`<ol class="pma-timeline">${items.map((e) => eventLine(e, !projectId))}</ol>`.value
    : emptyState(t('Noch keine Aktivität'), t('Änderungen erscheinen hier automatisch – als Verlauf, Audit-Log und Quelle für Integrationen.')).value;
}

export const icon = {
  plus: raw('<span aria-hidden="true">＋</span>'),
};

const SIGNAL_TAB: Record<string, string> = { task: 'tasks', gate: 'gates', risk: 'risks', decision: 'decisions', project: 'tasks' };

export interface SignalRow {
  code: string;
  severity: 'critical' | 'warning' | 'info';
  project_id: string;
  project_key: string;
  entity_type: string;
  title: string;
  detail: string;
}

/** Assistant hints: rule-based, each with its reason and a link to the record. */
export async function renderSignals(target: HTMLElement, projectId?: string): Promise<number> {
  const { items } = await api.get<{ items: SignalRow[] }>(`/signals${projectId ? `?projectId=${projectId}` : ''}`);
  const tone = { critical: 'danger', warning: 'warning', info: 'info' } as const;
  const label = { critical: t('Kritisch'), warning: t('Achtung'), info: t('Hinweis') };
  target.innerHTML = items.length
    ? html`<ul class="pma-list">${items.map(
        (s) => html`<li class="pma-list__row"><div>
            <a href="/projects/${s.project_id}?tab=${SIGNAL_TAB[s.entity_type] ?? 'overview'}">${s.title}</a>
            <div class="pma-muted pma-small">${projectId ? '' : `${s.project_key} · `}${s.detail}</div></div>
          <div class="pma-list__meta"><span class="p-badge p-badge--${tone[s.severity]}">${label[s.severity]}</span></div></li>`,
      )}</ul>`.value
    : emptyState(t('Keine Hinweise'), t('Termine, Gates, Risiken und Abwesenheiten sind aktuell unauffällig.')).value;
  return items.length;
}
